import { useApolloClient } from '@apollo/client/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowRight, Loader } from 'lucide-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Button } from '~/components/ui/button'
import { Form, FormControl, FormDescription, FormField, FormItem, FormMessage } from '~/components/ui/form'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { GQL_FIND_ORGANIZATION } from '~/gql/organizations/find-organization'

type Values = { organization: string }

type Props = {
  /** Slug d'une URL d'org qui n'existe pas : le champ s'ouvre dessus, avec l'erreur. */
  unknown?: string
  /** L'organisation existe : l'écran parent emmène le visiteur chez elle. */
  onFound: (slug: string) => void
}

/**
 * Le visiteur nomme son organisation (#346). Inconnue, l'erreur se pose sur le
 * champ, où le lecteur d'écran la lit avec lui ; une panne a son propre message,
 * pour ne pas faire croire qu'une organisation existante n'existe pas.
 */
function FindOrganizationForm({ unknown, onFound }: Props) {
  const { t } = useTranslation()
  const client = useApolloClient()
  const schema = z.object({ organization: z.string().trim().min(1, { message: t('input.required.message') }) })
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { organization: unknown ?? '' } })

  useEffect(() => {
    if (unknown) form.setError('organization', { message: t('findOrganization.error.unknown') })
  }, [unknown, form, t])

  const onSubmit = async ({ organization }: Values) => {
    try {
      const { data } = await client.query({
        query: GQL_FIND_ORGANIZATION,
        variables: { query: organization },
        fetchPolicy: 'network-only',
      })
      const slug = data?.findOrganization
      if (slug) onFound(slug)
      else form.setError('organization', { message: t('findOrganization.error.unknown') })
    } catch {
      form.setError('organization', { message: t('findOrganization.error.generic') })
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6" noValidate>
        <FormField
          control={form.control}
          name="organization"
          render={({ field }) => (
            <FormItem>
              <div className="grid gap-2">
                <Label htmlFor="organization" required>{t('findOrganization.field')}</Label>
                <FormControl>
                  <Input
                    id="organization"
                    aria-required
                    autoComplete="organization"
                    autoCapitalize="none"
                    spellCheck={false}
                    placeholder={t('findOrganization.placeholder')}
                    className="h-12"
                    {...field}
                  />
                </FormControl>
                <FormDescription className="text-xs">{t('findOrganization.hint')}</FormDescription>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" disabled={form.formState.isSubmitting} className="h-12 rounded-full">
          {form.formState.isSubmitting && <Loader className="mr-2 size-4 animate-spin" aria-hidden />}
          {t('findOrganization.submit')}
          {!form.formState.isSubmitting && <ArrowRight className="ml-2 size-4" aria-hidden />}
        </Button>
      </form>
    </Form>
  )
}

export default FindOrganizationForm
